import * as React from 'react';
import { connect } from 'react-redux';
import {
  getQuestionPoolSize,
  getMasteryScoreValue,
  isAttemptsLimited,
  getLimitedAttemptsNumber,
  getTimerEnabled,
  getTimerLimit,
  shouldSubmitAllQuestions
} from 'store/settings/selectors';
import { getName } from 'store/user/selectors';
import {
  getQuestions,
  hasCourseBeenStarted,
  getLastOpenedQuestion
} from 'store/questions/selectors';
import { getCourseAttempt, isInProgress } from 'store/course/selectors';
import { localize } from 'core/localization';
import { Separator } from 'components/common';
import { RootAppState } from 'store/types';
import { getTimeFromMilliseconds } from 'utils/timer';
import QuestionPoolOptionItem from './QuestionPoolOptionItem';
import {
  QuestionPoolWrapper,
  CourseUserTitle,
  CourseIntroductionText,
  QuestionPoolOptionsList,
  CourseStartButton
} from './QuestionPool.styled';
import { SKIP_TO_CONTENT_IDS } from 'constants/common';

type QuestionPoolProps = {
  questionPoolSize: number;
  questions: any[];
  hasCourseStarted: boolean;
  isCourseInProgress: boolean;
  navigateToPreviousPage(): void;
  isPreviousPageResults(): boolean;
  navigateToQuestion(id: string, question: string, isCourseOnOnePage: boolean): void;
  masteryScore?: number;
  isLimitedAttempts: boolean;
  limitedAttemptsNumber: number;
  courseAttempt?: number;
  userName?: string;
  isTimerEnabled: boolean;
  timeLimit?: number;
  courseAttempts: number;
  isSubmitAllQuestions: boolean;
  isCourseOnOnePage: boolean;
};

export class QuestionPool extends React.Component<QuestionPoolProps> {
  getTimerText = (isTimerEnabled: boolean) => {
    if (isTimerEnabled) {
      return localize('[min]');
    }
    return localize('[time]');
  };

  getTimer = (isTimerEnabled: boolean, timeLimit = 0) => {
    if (isTimerEnabled) {
      return timeLimit;
    }
    return -1;
  };

  continueCourse = () => {
    const {
      questions,
      isCourseInProgress,
      isPreviousPageResults,
      navigateToQuestion,
      navigateToPreviousPage,
      isCourseOnOnePage
    } = this.props;
    if (isPreviousPageResults() && isCourseInProgress) {
      const { id, sectionId } = getLastOpenedQuestion(questions);
      navigateToQuestion(sectionId, id, isCourseOnOnePage);
    } else {
      navigateToPreviousPage();
    }
  };

  startCourse = () => {
    const { questions, navigateToQuestion, isCourseOnOnePage } = this.props;
    const questionId = questions[0]?.id;
    const { sectionId } = questions[0];
    navigateToQuestion(sectionId, questionId, isCourseOnOnePage);
  };

  renderCourseIntroductionText = (remainedAttempts: number, isLimitedAttempts: boolean) => {
    const {
      questionPoolSize,
      masteryScore,
      timeLimit,
      isTimerEnabled = false,
      hasCourseStarted
    } = this.props;
    const introWithoutTimerAndAttempts = localize(
      '[question pool introduction text without timer and attempts x y]',
      {
        questionPoolSize,
        masteryScore
      }
    );
    const introWithTimerAndAttempts = localize(
      '[question pool introduction text with timer and attempts p q r s]',
      {
        questionPoolSize,
        timer: timeLimit,
        remainedAttempts,
        masteryScore
      }
    );
    const introWithTimer = localize('[question pool introduction text with timer x y z]', {
      questionPoolSize,
      timer: timeLimit,
      masteryScore
    });
    const introWithAttempts = localize('[question pool introduction text with attempts x y z]', {
      questionPoolSize,
      remainedAttempts,
      masteryScore
    });
    const timerWillStart = localize('[question pool timer intro text]');
    const goodLuck = localize('[good luck]');

    const introWithTimerText = () => {
      if (hasCourseStarted) {
        return introWithTimer;
      }
      return `${introWithTimer} ${timerWillStart} ${goodLuck}`;
    };

    const introWithAttemptsText = () => {
      if (hasCourseStarted) {
        return introWithAttempts;
      }
      return `${introWithAttempts} ${goodLuck}`;
    };

    const introWithTimerAndAttemptsText = () => {
      if (hasCourseStarted) {
        return introWithTimerAndAttempts;
      }
      return `${introWithTimerAndAttempts} ${timerWillStart} ${goodLuck}`;
    };

    const introWithoutTimerAndAttemptsText = () => {
      if (hasCourseStarted) {
        return introWithoutTimerAndAttempts;
      }
      return `${introWithoutTimerAndAttempts} ${goodLuck}`;
    };

    if (questionPoolSize <= 0) {
      return localize('[question pool introduction text zero question]');
    }

    if (isTimerEnabled && !isLimitedAttempts) {
      return introWithTimerText();
    }

    if (isLimitedAttempts && !isTimerEnabled) {
      return introWithAttemptsText();
    }

    if (isTimerEnabled && isLimitedAttempts) {
      return introWithTimerAndAttemptsText();
    }

    return introWithoutTimerAndAttemptsText();
  };

  render() {
    const {
      questionPoolSize,
      hasCourseStarted,
      userName,
      isTimerEnabled,
      timeLimit,
      isLimitedAttempts,
      limitedAttemptsNumber,
      courseAttempts,
      isSubmitAllQuestions
    } = this.props;

    const getRemainingAttempts = () => {
      if (isLimitedAttempts) {
        return isSubmitAllQuestions
          ? limitedAttemptsNumber - courseAttempts
          : limitedAttemptsNumber;
      }
      return -1;
    };
    return (
      <QuestionPoolWrapper>
        <CourseUserTitle>
          {userName
            ? localize('[hi username]', {
                userName
              })
            : localize('[hi there]')}
          ,
        </CourseUserTitle>
        <CourseIntroductionText>
          {this.renderCourseIntroductionText(getRemainingAttempts(), isLimitedAttempts)}
        </CourseIntroductionText>
        <QuestionPoolOptionsList>
          <QuestionPoolOptionItem
            iconName="document-alt"
            optionValue={questionPoolSize}
            optionText={localize('[questions]')}
          />
          <QuestionPoolOptionItem
            iconName="clock"
            optionValue={this.getTimer(isTimerEnabled, timeLimit)}
            optionText={this.getTimerText(isTimerEnabled)}
          />
          <QuestionPoolOptionItem
            iconName="repeat"
            optionValue={getRemainingAttempts()}
            optionText={localize('[attempts]')}
          />
        </QuestionPoolOptionsList>
        <Separator opacity={0.05} color="textColor" height={1} margin={0} />
        <CourseStartButton
          id={SKIP_TO_CONTENT_IDS.QUESTION_POOL_START_BUTTON}
          onClick={hasCourseStarted ? this.continueCourse : this.startCourse}
          tabIndex={0}
          disabled={questionPoolSize <= 0}
        >
          {hasCourseStarted
            ? localize('[continue course]')
            : localize('[course ready to start text]')}
        </CourseStartButton>
      </QuestionPoolWrapper>
    );
  }
}

function mapStateToProps(state: RootAppState) {
  return {
    timeLimit: getTimeFromMilliseconds(getTimerLimit(state), 'min') as number,
    questions: getQuestions(state),
    questionPoolSize: getQuestionPoolSize(state),
    masteryScore: getMasteryScoreValue(state),
    hasCourseStarted: hasCourseBeenStarted(state),
    isCourseInProgress: isInProgress(state),
    userName: getName(state),
    isLimitedAttempts: isAttemptsLimited(state),
    limitedAttemptsNumber: getLimitedAttemptsNumber(state),
    isTimerEnabled: getTimerEnabled(state),
    courseAttempts: getCourseAttempt(state),
    isSubmitAllQuestions: shouldSubmitAllQuestions(state)
  };
}

export default connect(mapStateToProps)(QuestionPool);
